import { clamp01 } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { SkeletonValues } from './params';

/**
 * Reduces the letters to their medial axis and redraws them as strokes.
 *
 * The medial axis is found from the distance field rather than by morphological
 * thinning: a point is on the axis where its distance to the contour is a local
 * maximum along the gradient direction — the ridge of the field. That is far cheaper
 * than iterative erosion and, more usefully, it comes with the *thickness* at every
 * point for free, which is what lets the redrawn stroke vary in width the way the
 * original letterform does.
 */
export interface SkeletonStroke {
  points: Float32Array;
  /** Local glyph half-thickness at each point. */
  thickness: Float32Array;
  length: number;
}

export interface SkeletonState {
  strokes: SkeletonStroke[];
  palette: ResolvedPalette;
  widthCurve: Float32Array;
  maxThickness: number;
  built: boolean;
}

function values(ctx: TechniqueContext): SkeletonValues {
  return ctx.values as unknown as SkeletonValues;
}

export function init(ctx: TechniqueContext): SkeletonState {
  const v = values(ctx);
  return {
    strokes: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    widthCurve: bakeCurve(v.widthCurve as Curve),
    maxThickness: 1,
    built: false,
  };
}

/** Douglas-Peucker, iterative to avoid deep recursion on long chains. */
function simplify(points: number[], tolerance: number): number[] {
  const n = points.length / 2;
  if (n < 3 || tolerance <= 0) return points;

  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  const tol2 = tolerance * tolerance;

  while (stack.length) {
    const [start, end] = stack.pop() as [number, number];
    if (end - start < 2) continue;

    const ax = points[start * 2];
    const ay = points[start * 2 + 1];
    const bx = points[end * 2];
    const by = points[end * 2 + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSquared = dx * dx + dy * dy || 1;

    let worst = -1;
    let worstDistance = 0;
    for (let i = start + 1; i < end; i++) {
      const px = points[i * 2];
      const py = points[i * 2 + 1];
      const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
      const ex = ax + dx * t - px;
      const ey = ay + dy * t - py;
      const d = ex * ex + ey * ey;
      if (d > worstDistance) {
        worstDistance = d;
        worst = i;
      }
    }

    if (worst >= 0 && worstDistance > tol2) {
      keep[worst] = 1;
      stack.push([start, worst], [worst, end]);
    }
  }

  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(points[i * 2], points[i * 2 + 1]);
  return out;
}

export function step(state: SkeletonState, _dt: number, ctx: TechniqueContext): SkeletonState {
  if (state.built) return state;
  const v = values(ctx);

  const cell = Math.max(1, v.resolution);
  const bounds = ctx.field.bounds;
  if (bounds.width <= 0 || bounds.height <= 0) {
    state.built = true;
    return state;
  }

  const cols = Math.ceil(bounds.width / cell) + 1;
  const rows = Math.ceil(bounds.height / cell) + 1;
  if (cols * rows > 4_000_000) {
    state.built = true;
    return state;
  }

  // ── Ridge detection ──────────────────────────────────────────────────────
  const depth = new Float32Array(cols * rows);
  const ridge = new Uint8Array(cols * rows);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = bounds.x + c * cell;
      const y = bounds.y + r * cell;
      depth[r * cols + c] = Math.max(0, -ctx.field.distance(x, y));
    }
  }

  const threshold = v.threshold * cell;
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      const i = r * cols + c;
      const d = depth[i];
      if (d < cell) continue;

      // A ridge point is a local maximum along at least one axis. Requiring both would
      // only find isolated peaks, not the connected axis of a stem.
      const horizontal = d >= depth[i - 1] && d >= depth[i + 1] && d - Math.min(depth[i - 1], depth[i + 1]) > -threshold;
      const vertical = d >= depth[i - cols] && d >= depth[i + cols] && d - Math.min(depth[i - cols], depth[i + cols]) > -threshold;
      const diagA = d >= depth[i - cols - 1] && d >= depth[i + cols + 1];
      const diagB = d >= depth[i - cols + 1] && d >= depth[i + cols - 1];

      if ((horizontal && vertical) || (horizontal && (diagA || diagB)) || (vertical && (diagA || diagB))) {
        ridge[i] = 1;
      }
    }
  }

  // ── Trace ridge pixels into polylines ────────────────────────────────────
  const visited = new Uint8Array(cols * rows);
  const neighbourOffsets = [-cols - 1, -cols, -cols + 1, -1, 1, cols - 1, cols, cols + 1];

  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      const start = r * cols + c;
      if (!ridge[start] || visited[start]) continue;

      // Walk greedily to the nearest unvisited ridge neighbour, which traces the
      // connected axis without needing a full graph.
      const points: number[] = [];
      const thickness: number[] = [];
      let current = start;

      while (current >= 0 && !visited[current]) {
        visited[current] = 1;
        const cc = current % cols;
        const rr = (current / cols) | 0;
        points.push(bounds.x + cc * cell, bounds.y + rr * cell);
        thickness.push(depth[current]);

        let next = -1;
        for (const offset of neighbourOffsets) {
          const candidate = current + offset;
          if (candidate < 0 || candidate >= ridge.length) continue;
          if (!ridge[candidate] || visited[candidate]) continue;
          next = candidate;
          break;
        }
        current = next;
      }

      if (points.length < 6) continue;

      let length = 0;
      for (let i = 2; i < points.length; i += 2) {
        length += Math.hypot(points[i] - points[i - 2], points[i + 1] - points[i - 1]);
      }
      if (length < v.minLength) continue;

      const simplified = simplify(points, v.simplify);
      if (simplified.length < 4) continue;

      // Resample the thickness onto the simplified points by nearest original index.
      const thick = new Float32Array(simplified.length / 2);
      for (let i = 0; i < thick.length; i++) {
        const t = i / Math.max(1, thick.length - 1);
        thick[i] = thickness[Math.min(thickness.length - 1, Math.round(t * (thickness.length - 1)))];
        if (thick[i] > state.maxThickness) state.maxThickness = thick[i];
      }

      state.strokes.push({ points: Float32Array.from(simplified), thickness: thick, length });
    }
  }

  state.built = true;
  return state;
}

export function isComplete(state: SkeletonState): boolean {
  return state.built;
}

export function draw(state: SkeletonState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineCap = v.cap as CanvasLineCap;
  c.lineJoin = 'round';

  state.strokes.forEach((stroke, strokeIndex) => {
    const n = stroke.points.length / 2;
    if (n < 2) return;

    for (let i = 1; i < n; i++) {
      const t = i / (n - 1);
      const curve = clamp01(sampleBaked(state.widthCurve, t));
      const thicknessFactor = v.widthFromDistance ? clamp01(stroke.thickness[i] / state.maxThickness) : 1;
      const width = Math.max(0.2, v.width * curve * (v.widthFromDistance ? 0.25 + thicknessFactor * 1.5 : 1));

      let colorT: number;
      if (v.colorMode === 'thickness') colorT = thicknessFactor;
      else if (v.colorMode === 'stroke') colorT = strokeIndex / Math.max(1, state.strokes.length - 1);
      else colorT = 0;

      c.strokeStyle = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(colorT, v.opacity);
      c.lineWidth = width;
      c.beginPath();
      c.moveTo(
        stroke.points[(i - 1) * 2] + (v.jitter ? ctx.rng.signed(v.jitter) : 0),
        stroke.points[(i - 1) * 2 + 1] + (v.jitter ? ctx.rng.signed(v.jitter) : 0),
      );
      c.lineTo(
        stroke.points[i * 2] + (v.jitter ? ctx.rng.signed(v.jitter) : 0),
        stroke.points[i * 2 + 1] + (v.jitter ? ctx.rng.signed(v.jitter) : 0),
      );
      c.stroke();
    }
  });
}

export function elementCount(state: SkeletonState): number {
  return state.strokes.length;
}
